<script lang="ts" module>
	import type { Chart, ChartData, ChartOptions, Point, TooltipItem } from 'chart.js';

	let chartModule: typeof import('chart.js') | null = null;
	let loadingPromise: Promise<typeof import('chart.js')> | null = null;

	function loadChartJS(): Promise<typeof import('chart.js')> {
		if (chartModule) return Promise.resolve(chartModule);
		if (loadingPromise) return loadingPromise;

		loadingPromise = import('chart.js').then((module) => {
			module.Chart.register(...module.registerables);
			chartModule = module;
			return module;
		});

		return loadingPromise;
	}
</script>

<script lang="ts">
	import { onMount, onDestroy } from 'svelte';

	interface Props {
		data?: Point[][];
		showLegend?: boolean;
		datasetDisplayConfig?: Array<{ borderColor: string; backgroundColor: string; label: string }>;
		formatTooltip?: (value: number, dataLabel: string) => string;
		formatYAxis?: (value: number) => string;
		formatXAxis?: (value: number) => string;
		tooltipFooter?: (data: TooltipItem<'line'>[]) => void;
	}

	let {
		data = [],
		showLegend = false,
		datasetDisplayConfig = [
			{
				borderColor: 'rgb(99, 102, 241)',
				backgroundColor: 'rgba(99, 102, 241, 0.2)',
				label: 'Series 1'
			}
		],
		formatTooltip = (v) => v.toString(),
		formatYAxis = (v) => v.toString(),
		formatXAxis = (v) => v.toString(),
		tooltipFooter = () => {}
	}: Props = $props();

	let canvas: HTMLCanvasElement = $state()!;
	let chart: Chart<'line'> | null = $state(null);
	let mounted = $state(false);
	let initializing = $state(false);

	function datasetConfig(c: { label: string; borderColor: string; backgroundColor: string }) {
		return {
			label: c.label,
			data: [],
			borderColor: c.borderColor,
			backgroundColor: c.backgroundColor,
			fill: true,
			tension: 0.3
		};
	}

	function getChartConfig(): { data: ChartData<'line'>; options: ChartOptions<'line'> } {
		return {
			data: {
				datasets: datasetDisplayConfig.map(datasetConfig)
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				interaction: {
					intersect: false,
					mode: 'index'
				},
				plugins: {
					legend: {
						display: showLegend,
						labels: {
							usePointStyle: true,
							pointStyle: 'line'
						}
					},
					tooltip: {
						callbacks: {
							title: (context) => {
								// Chart.js typed dataset.raw as `unknown`; we know the
								// data is a `{x, y}` object on this LineChart consumer.
								const raw = context[0].raw as { x: number };
								return formatXAxis(raw.x);
							},
							label: (context) => formatTooltip(context.parsed.y ?? 0, context.dataset.label ?? ''),
							labelPointStyle: (_context) => ({
								pointStyle: 'line',
								rotation: 0
							}),
							footer: tooltipFooter
						},
						usePointStyle: true
					}
				},
				scales: {
					x: {
						type: 'linear',
						bounds: 'data',
						display: true,
						grid: {
							display: true
						},
						ticks: {
							maxRotation: 0,
							autoSkip: true,
							maxTicksLimit: 13,
							callback: (value) => formatXAxis(Number(value))
						}
					},
					y: {
						stacked: true,
						display: true,
						beginAtZero: true,
						ticks: {
							callback: (value) => formatYAxis(Number(value))
						}
					}
				}
			}
		};
	}

	async function initChart() {
		if (!canvas || initializing) return;

		// Destroy existing chart first
		if (chart) {
			chart.destroy();
			chart = null;
		}

		initializing = true;

		try {
			const { Chart } = await loadChartJS();
			const ctx = canvas.getContext('2d');
			if (!ctx || !mounted) return;

			const config = getChartConfig();
			chart = new Chart(ctx, {
				type: 'line',
				...config
			});
		} finally {
			initializing = false;
		}
	}

	function updateChart() {
		if (!chart || initializing) return;
		for (let i = 0; i < data.length; i++) {
			chart.data.datasets[i].data = data[i];
		}
		chart.update('none');
	}

	onMount(() => {
		mounted = true;
		initChart();
	});

	onDestroy(() => {
		mounted = false;
		if (chart) {
			chart.destroy();
			chart = null;
		}
	});

	// Chart re-render side effect (per PATTERNS § $:-side-effect-callout): MUST be
	// $effect, NOT $derived. Chart.js instance creation stays in onMount per RUNES-05;
	// only the data-update side effect moves here.
	$effect(() => {
		if (mounted && data) {
			if (chart) {
				updateChart();
			} else if (!initializing) {
				initChart();
			}
		}
	});
</script>

<canvas bind:this={canvas}></canvas>
